import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { PageHeader, Loading, Field, ErrBox } from "../../components/common";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("company");
  const [company, setCompany] = useState({});
  const [smsKey, setSmsKey] = useState("");
  const [stripeKey, setStripeKey] = useState("");
  const [smsPhone, setSmsPhone] = useState("");
  const [smsResult, setSmsResult] = useState(null);
  const [legal, setLegal] = useState(null);
  const [legalBody, setLegalBody] = useState("");
  const [prodUri, setProdUri] = useState("");
  const [prodDb, setProdDb] = useState("");
  const [mergeCase, setMergeCase] = useState("AZTA-CLIENT-100270");
  const [mergeLog, setMergeLog] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [connStatus, setConnStatus] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/admin/settings").then((r) => { setData(r.data); setCompany(r.data.settings.company || {}); });
  useEffect(() => { load(); }, []);

  if (!data) return <Loading label="Loading settings" />;
  const s = data.settings;

  const save = async (payload, msg) => {
    setBusy(true); setErr("");
    try {
      await API.put("/admin/settings", payload);
      toast.success(msg);
      load();
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const testSms = async () => {
    setBusy(true); setSmsResult(null); setErr("");
    try {
      const { data: res } = await API.post("/admin/settings/sms/test", { phone: smsPhone });
      setSmsResult(res);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const TABS = [["company", "Company"], ["tax", "Tax Settings"], ["sms", "SMS (TextBelt)"],
    ["stripe", "Stripe / Payments"], ["sync", "Data Sync / Deploy Merge"], ["demo", "Demo Mode"], ["legal", "Legal Documents"]];

  const mergePayload = (extra) => ({
    ...extra,
    ...(prodUri.trim() ? { prod_uri: prodUri, ...(prodDb.trim() ? { prod_db: prodDb } : {}), save_config: true } : {}),
  });

  const testConnection = async () => {
    setMergeBusy(true); setErr(""); setConnStatus(""); setMergeLog("");
    try {
      const { data: res } = await API.post("/admin/migration/merge", mergePayload({ test_connection: true }));
      setConnStatus(res.message);
      toast.success("Production connection verified");
      setProdUri(""); load();
    } catch (e) { setErr(errMsg(e)); }
    setMergeBusy(false);
  };

  const verifyMerge = async () => {
    setMergeBusy(true); setErr(""); setVerifyResult(null);
    try {
      const { data: res } = await API.post("/admin/migration/verify", { case_number: mergeCase });
      setVerifyResult(res);
      if (res.status === "ok") toast.success("Snapshot loaded — this shows what THIS site's database holds");
      else toast.error(res.message);
    } catch (e) { setErr(errMsg(e)); }
    setMergeBusy(false);
  };

  const downloadPackage = async () => {
    setMergeBusy(true); setErr(""); setMergeLog(""); setConnStatus("");
    try {
      const res = await API.post("/admin/migration/export", { case_number: mergeCase }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `azta-merge-${mergeCase}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Merge package downloaded — now import it on your live site");
    } catch (e) {
      let msg = "Export failed";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      } else { msg = errMsg(e); }
      setErr(msg);
    }
    setMergeBusy(false);
  };

  const importPackage = async (file) => {
    if (!file) return;
    setMergeBusy(true); setErr(""); setMergeLog(""); setConnStatus("");
    try {
      const pkg = JSON.parse(await file.text());
      const { data: res } = await API.post("/admin/migration/import", { package: pkg });
      setMergeLog(res.log);
      if (res.verification) setVerifyResult(res.verification);
      if (res.case_number) setMergeCase(res.case_number);
      toast.success(`Import complete for ${res.case_number || pkg.case_number || "merge package"}`);
    } catch (e) {
      setErr(e instanceof SyntaxError ? "That file isn't valid JSON — choose the .json merge package you downloaded." : errMsg(e));
    }
    setMergeBusy(false);
  };

  const runMerge = async (dryRun) => {
    setMergeBusy(true); setMergeLog(""); setErr(""); setConnStatus("");
    try {
      const { data: res } = await API.post("/admin/migration/merge", mergePayload({ case_number: mergeCase, dry_run: dryRun }));
      setMergeLog(res.log);
      if (!dryRun) toast.success(`Merge applied for ${mergeCase}`);
      setProdUri(""); load();
    } catch (e) { setErr(errMsg(e)); }
    setMergeBusy(false);
  };

  return (
    <div data-testid="admin-settings">
      <PageHeader title="System Settings" sub="Company, tax engine, messaging, payments, demo data, and legal language." />
      <div className="mb-5 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`asettings-tab-${v}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === v ? "bg-navy text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "company" && (
        <div className="card-gov max-w-2xl space-y-4 p-6" data-testid="company-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company Name"><input className="input-gov" value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="co-name" /></Field>
            <Field label="Phone"><input className="input-gov" value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} data-testid="co-phone" /></Field>
            <Field label="Support Email"><input className="input-gov" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} data-testid="co-email" /></Field>
            <Field label="Address"><input className="input-gov" value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} data-testid="co-address" /></Field>
          </div>
          <ErrBox msg={err} />
          <button className="btn-navy" disabled={busy} onClick={() => save({ company }, "Company info saved")} data-testid="co-save">Save Company Info</button>
        </div>
      )}

      {tab === "tax" && (
        <div className="card-gov max-w-2xl space-y-4 p-6" data-testid="tax-settings-form">
          <p className="text-sm text-slate-600">Penalty and interest rates used when estimating past-due exposure. Rates are labeled as estimates until verified.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Penalty %/month"><input type="number" step="0.1" className="input-gov" defaultValue={s.tax?.penalty_rate_monthly} id="pen" data-testid="tax-penalty" /></Field>
            <Field label="Penalty Cap %"><input type="number" step="0.1" className="input-gov" defaultValue={s.tax?.penalty_cap} id="cap" data-testid="tax-cap" /></Field>
            <Field label="Interest %/year"><input type="number" step="0.1" className="input-gov" defaultValue={s.tax?.interest_annual} id="int" data-testid="tax-interest" /></Field>
          </div>
          <ErrBox msg={err} />
          <button className="btn-navy" disabled={busy} data-testid="tax-save"
            onClick={() => save({ tax: { penalty_rate_monthly: parseFloat(document.getElementById("pen").value), penalty_cap: parseFloat(document.getElementById("cap").value), interest_annual: parseFloat(document.getElementById("int").value) } }, "Tax settings saved")}>
            Save Tax Settings
          </button>
        </div>
      )}

      {tab === "sms" && (
        <div className="card-gov max-w-2xl space-y-4 p-6" data-testid="sms-form">
          <p className="text-sm text-slate-600">SMS notifications via TextBelt. {s.textbelt_key_set ? "A key is configured." : "No key configured — the free test key (1 SMS/day) is used."} Keys are stored server-side only and never shown after saving.</p>
          <Field label="TextBelt API Key"><input className="input-gov font-mono" placeholder={s.textbelt_key_set ? "•••••••• (key set)" : "paste key from textbelt.com"} value={smsKey} onChange={(e) => setSmsKey(e.target.value)} data-testid="sms-key" /></Field>
          <button className="btn-navy" disabled={busy || !smsKey} onClick={() => save({ textbelt_key: smsKey }, "SMS key saved")} data-testid="sms-save">Save Key</button>
          <div className="border-t border-slate-200 pt-4">
            <Field label="Send Test SMS To (E.164, e.g. +16025550148)"><input className="input-gov" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} data-testid="sms-test-phone" /></Field>
            <button className="btn-outline-gov mt-2" disabled={busy || !smsPhone} onClick={testSms} data-testid="sms-test-send">{busy ? "Sending…" : "Send Test SMS"}</button>
            {smsResult && (
              <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${smsResult.success ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid="sms-test-result">
                {smsResult.success ? `Accepted by TextBelt (ID ${smsResult.textId}, status: ${smsResult.deliveryStatus}, quota left: ${smsResult.quotaRemaining})` : `Failed: ${smsResult.error}`}
              </div>
            )}
            <ErrBox msg={err} />
          </div>
        </div>
      )}

      {tab === "stripe" && (
        <div className="card-gov max-w-2xl space-y-4 p-6" data-testid="stripe-form">
          <p className="text-sm text-slate-600">
            Subscription billing runs on Stripe. A managed test sandbox is active
            {s.stripe_key_set ? " — a custom key override is also configured." : "."}
            Paste your own live secret key here when you're ready to bill real customers; it is stored server-side and never displayed.
          </p>
          <Field label="Stripe Secret Key Override"><input className="input-gov font-mono" placeholder={s.stripe_key_set ? "•••••••• (override set)" : "sk_live_… (optional)"} value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} data-testid="stripe-key" /></Field>
          <button className="btn-navy" disabled={busy || !stripeKey} onClick={() => save({ stripe_secret_key_override: stripeKey }, "Stripe key saved")} data-testid="stripe-save">Save Key</button>
        </div>
      )}

      {tab === "sync" && (
        <div className="max-w-3xl space-y-6" data-testid="sync-panel">
          <div className="card-gov space-y-4 p-6">
            <div>
              <h3 className="font-bold text-navy">Merge Accounts to the Live Site — 3 Clicks, Always Works</h3>
              <p className="mt-1 text-sm text-slate-600">
                Your live site's database is managed by Emergent and blocks outside connections by design — so the merge
                travels through the apps themselves: download a package here, import it on the live site. Nothing on the
                live site is ever deleted or overwritten; records that already exist are skipped.
              </p>
            </div>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-700">
              <li>In <b>Manage Publishing</b>, click <b>Re-publish changes</b> (one time, so the live site has the importer).</li>
              <li>Here on the preview site: click <b>Download Merge Package</b> — a .json file downloads to your computer.</li>
              <li>Open your <b>live site</b> → Admin → Settings → this same Data Sync tab → <b>Import Merge Package</b> → pick that file. Done.</li>
            </ol>
            <Field label="Case Number to Merge"><input className="input-gov max-w-sm font-mono" value={mergeCase} onChange={(e) => setMergeCase(e.target.value)} data-testid="merge-case" /></Field>
            <div className="flex flex-wrap gap-2">
              <button className="btn-navy" disabled={mergeBusy} onClick={downloadPackage} data-testid="download-package-btn">
                {mergeBusy ? "Working…" : "1. Download Merge Package"}
              </button>
              <label className={`btn-copper cursor-pointer ${mergeBusy ? "pointer-events-none opacity-50" : ""}`} data-testid="import-package-btn">
                2. Import Merge Package
                <input type="file" accept=".json,application/json" className="hidden" disabled={mergeBusy}
                  onChange={(e) => { importPackage(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-package-input" />
              </label>
              <button className="btn-outline-gov" disabled={mergeBusy} onClick={verifyMerge} data-testid="verify-merge-btn">
                {mergeBusy ? "Working…" : "3. Verify Merge"}
              </button>
            </div>
          </div>

          <details className="card-gov p-6" data-testid="direct-connection-panel">
            <summary className="cursor-pointer text-sm font-bold text-navy">Advanced: Direct Database Connection (only if you run your own MongoDB Atlas)</summary>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Paste a self-managed Atlas connection string once and merge directly. Emergent-managed production
                databases block outside connections — for those, use the package method above.
              </p>
              {s.prod_mongo_configured && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="prod-configured-banner">
                  Production database is saved{typeof s.prod_db_name === "string" && s.prod_db_name ? ` (${s.prod_db_name})` : ""}. You can merge with one click below — no need to paste anything again.
                </div>
              )}
              <Field label="Production Connection Details" hint='Paste the MONGO_URL line from Manage Publishing → Secrets → System keys. Pasting the whole line (MONGO_URL="mongodb+srv://…") or both lines including DB_NAME=… works too.'>
                <textarea className="input-gov font-mono text-xs" rows={3} placeholder={'MONGO_URL="mongodb+srv://user:pass@cluster.mongodb.net/..."'}
                  value={prodUri} onChange={(e) => setProdUri(e.target.value)} data-testid="prod-uri" />
              </Field>
              <Field label="Database Name (optional)" hint="Leave blank — auto-detected when you test the connection. Don't type the words DB_NAME; only its value goes here.">
                <input className="input-gov max-w-sm font-mono" placeholder="Leave blank to auto-detect" value={prodDb} onChange={(e) => setProdDb(e.target.value)} data-testid="prod-db" />
              </Field>
              <div className="flex flex-wrap gap-2">
                <button className="btn-outline-gov" disabled={mergeBusy || (!prodUri.trim() && !s.prod_mongo_configured)} onClick={testConnection} data-testid="test-connection-btn">
                  {mergeBusy ? "Working…" : s.prod_mongo_configured ? "Test Saved Connection" : "Save & Test Connection"}
                </button>
                <button className="btn-outline-gov" disabled={mergeBusy} onClick={() => runMerge(true)} data-testid="merge-dry-run">
                  {mergeBusy ? "Running…" : "Preview Merge (Dry Run)"}
                </button>
                <button className="btn-copper" disabled={mergeBusy} onClick={() => { if (window.confirm(`Apply merge for ${mergeCase} to production? Existing production records are kept.`)) runMerge(false); }} data-testid="merge-live">
                  {mergeBusy ? "Merging…" : "Merge to Production"}
                </button>
              </div>
              {connStatus && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="conn-status">{connStatus}</div>
              )}
            </div>
          </details>

          {verifyResult && (
            <div className="rounded-md border border-slate-200 bg-white p-4 text-sm" data-testid="verify-result">
              {verifyResult.status === "ok" ? (
                <>
                  <p className="font-bold text-navy">On THIS site: {verifyResult.legal_name || "(no company name)"} — {verifyResult.case_number}</p>
                  <p className="mt-1 text-slate-600">
                    Owners: {verifyResult.owners.length ? verifyResult.owners.map((o) => `${o.email} — ${o.total_businesses_linked} business${o.total_businesses_linked === 1 ? "" : "es"} linked`).join("; ") : "none linked"}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs sm:grid-cols-3">
                    {Object.entries(verifyResult.record_counts).map(([k, v]) => <div key={k}>{k}: <b>{v}</b></div>)}
                    {Object.keys(verifyResult.record_counts).length === 0 && (
                      <div className="col-span-3 font-sans font-semibold text-red-700">No financial records found for this business in this database.</div>
                    )}
                  </div>
                </>
              ) : (
                <p className="font-semibold text-red-700">{verifyResult.message}</p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Run this on the LIVE site to check production. If an owner shows 2+ businesses linked, re-run the import — it automatically removes empty registration shells.
              </p>
            </div>
          )}

          {mergeLog && (
            <pre className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-slate-900 p-4 font-mono text-xs text-emerald-300" data-testid="merge-log">{mergeLog}</pre>
          )}
          <ErrBox msg={err} />
        </div>
      )}

      {tab === "demo" && (
        <div className="card-gov max-w-2xl space-y-4 p-6" data-testid="demo-form">
          <p className="text-sm text-slate-600">Demo mode includes the demo business (Copper Canyon Coffee Roasters LLC) and demo accounts in admin statistics. Demo data is flagged <span className="rounded bg-gold/20 px-1 text-xs font-bold text-amber-700">DEMO</span> everywhere and never mixes with real customers.</p>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 p-4">
            <input type="checkbox" checked={!!s.demo_mode} onChange={(e) => save({ demo_mode: e.target.checked }, e.target.checked ? "Demo mode ON" : "Demo mode OFF")} data-testid="demo-toggle" />
            <span className="text-sm font-semibold text-slate-700">Demo mode enabled (currently {s.demo_mode ? "ON" : "OFF"})</span>
          </label>
        </div>
      )}

      {tab === "legal" && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="card-gov p-2">
            {data.legal_documents.map((d) => (
              <button key={d.slug} onClick={() => { setLegal(d); setLegalBody(d.body); }} data-testid={`legal-${d.slug}`}
                className={`block w-full rounded px-3 py-2 text-left text-sm font-medium ${legal?.slug === d.slug ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {d.title} <span className="text-xs opacity-60">v{d.version}</span>
              </button>
            ))}
          </div>
          <div className="card-gov p-6 lg:col-span-2">
            {legal ? (
              <div className="space-y-4">
                <h3 className="font-bold text-navy">{legal.title}</h3>
                <textarea className="input-gov" rows={10} value={legalBody} onChange={(e) => setLegalBody(e.target.value)} data-testid="legal-body" />
                <button className="btn-navy" disabled={busy} data-testid="legal-save"
                  onClick={async () => {
                    setBusy(true); setErr("");
                    try {
                      const { data: res } = await API.put(`/admin/settings/legal/${legal.slug}`, { body: legalBody });
                      toast.success(`Saved — now version ${res.version}`);
                      load();
                    } catch (e) { setErr(errMsg(e)); }
                    setBusy(false);
                  }}>Save (creates new version)</button>
                <ErrBox msg={err} />
              </div>
            ) : <p className="text-sm text-slate-500">Select a document to edit its language. Changes are versioned and audit-logged.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
